'use client';

import React, { useEffect, useRef, useState } from 'react';
import { gridMetrics, gridX, cellExpr } from './HeroGrid';
import CubeCluster from './CubeCluster';
import StarMarker from './StarMarker';
import DashedPanel from './DashedPanel';
import FloatingLabel from '../FloatingLabel';
import Cubes from '../Cubes';

/** A decoration to place somewhere in the field. Position is assigned at runtime. */
type Item =
  | { type: 'cube'; variant: 'a' | 'b' | 'c' | 'd' }
  | { type: 'cube3d' }
  | { type: 'star'; size: number }
  | { type: 'panel'; label: string }
  | { type: 'label'; text: string; position: 'top' | 'bottom' };

/** Fixed inventory — roughly today's density. Placement is randomized, not the set. */
const INVENTORY: Item[] = [
  { type: 'cube3d' },
  { type: 'cube3d' },
  { type: 'cube', variant: 'a' },
  { type: 'cube', variant: 'c' },
  { type: 'cube', variant: 'b' },
  { type: 'panel', label: '[1×1]' },
  { type: 'panel', label: '[1×1]' },
  { type: 'star', size: 36 },
  { type: 'star', size: 32 },
  { type: 'star', size: 34 },
  { type: 'star', size: 30 },
  { type: 'label', text: 'ROUTE', position: 'top' },
  { type: 'label', text: '301', position: 'bottom' },
  { type: 'label', text: 'HTTPS', position: 'top' },
  { type: 'label', text: 'QR', position: 'bottom' },
  { type: 'label', text: 'EMAIL', position: 'bottom' },
  { type: 'label', text: '.JSON', position: 'top' },
  { type: 'label', text: 'API', position: 'top' },
];

type Cell = { g: number; r: number; w: number };
type Placement = Item & { id: number; gcol: number; row: number; key: number; delay: number; leaving?: boolean };

/** Only a subset of the inventory is on screen at once; on each tick one item
 *  fades out and a different hidden one fades in elsewhere — so the field keeps
 *  changing instead of showing every decoration (and animation) simultaneously. */
const MAX_VISIBLE = 12;
const TICK_MS = 2800;
const FADE_OUT_MS = 650;

const HeroDecorations: React.FC = () => {
  const rootRef = useRef<HTMLDivElement>(null);
  const poolRef = useRef<Cell[]>([]);
  const keyRef = useRef(1);
  const [placements, setPlacements] = useState<Placement[]>([]);

  // ---- placement maths -----------------------------------------------------

  // Build the candidate-cell pool: every cell across the FULL viewport width
  // and the hero's height, minus the cells covered by content (measured live)
  // and the top row (behind the fixed navbar). Cells are weighted toward the
  // outer columns so the field actually reaches the screen edges.
  const computePool = (): Cell[] => {
    const root = rootRef.current;
    if (!root) return [];
    const w = document.documentElement.clientWidth;
    const { cell, offsetX } = gridMetrics(w);
    const layerTop = root.getBoundingClientRect().top;
    const layerH = root.getBoundingClientRect().height;

    const forbidden = new Set<string>();
    const addRect = (rect: DOMRect, padX: number, padY: number) => {
      const g0 = Math.floor((rect.left - padX * cell - offsetX) / cell);
      const g1 = Math.ceil((rect.right + padX * cell - offsetX) / cell);
      const r0 = Math.floor((rect.top - padY * cell - layerTop) / cell);
      const r1 = Math.ceil((rect.bottom + padY * cell - layerTop) / cell);
      for (let r = r0; r < r1; r++) for (let g = g0; g < g1; g++) forbidden.add(`${g},${r}`);
    };
    // Text uses tight per-line rects (so centered headings don't forbid the whole
    // row); boxes use their bounding rect.
    const addText = (el: Element | null, padX: number, padY: number) => {
      if (!el) return;
      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = Array.from(range.getClientRects());
      if (rects.length) rects.forEach((rc) => addRect(rc as DOMRect, padX, padY));
      else addRect(el.getBoundingClientRect(), padX, padY);
    };
    const addBox = (el: Element | null, padX: number, padY: number) => {
      if (el) addRect(el.getBoundingClientRect(), padX, padY);
    };

    // Smallest matching element = the innermost one that still holds the text
    // (avoids matching a huge ancestor that merely contains the phrase).
    const byText = (sel: string, needle: RegExp) => {
      const matches = Array.from(document.querySelectorAll(sel)).filter((e) =>
        needle.test(e.textContent || ''),
      );
      if (!matches.length) return null;
      return matches.reduce((best, e) => {
        const a = e.getBoundingClientRect();
        const b = best.getBoundingClientRect();
        return a.width * a.height < b.width * b.height ? e : best;
      });
    };

    addText(document.querySelector('h1'), 0.5, 0.35);
    addText(document.querySelector('p[class*="max-w-2xl"]'), 0.4, 0.3);
    addBox(document.querySelector('[class*="max-w-3xl"]'), 0.4, 0.3);
    addBox(byText('div', /Smart Routing Rules/), 0.4, 0.3);
    addText(byText('p', /Running links for/i), 0.3, 0.25);
    // Company logos row: forbid each logo's box (the row container is full-width).
    const logos = document.querySelector('[data-customer-logos]');
    if (logos) Array.from(logos.children).forEach((c) => addBox(c, 0.3, 0.25));

    const gColMin = Math.ceil(-offsetX / cell);
    const gColMax = Math.floor((w - offsetX) / cell) - 1;
    const rows = Math.max(3, Math.floor(layerH / cell));
    const centerG = (gColMin + gColMax) / 2;
    const halfSpan = Math.max(1, (gColMax - gColMin) / 2);

    const pool: Cell[] = [];
    for (let r = 1; r < rows; r++) {
      for (let g = gColMin; g <= gColMax; g++) {
        if (forbidden.has(`${g},${r}`)) continue;
        // 0.12 floor keeps inner cells possible; the ^1.6 curve heavily favors edges.
        const edge = Math.pow(Math.abs(g - centerG) / halfSpan, 1.6);
        pool.push({ g, r, w: 0.12 + edge });
      }
    }
    return pool;
  };

  // Weighted pick of one cell from `avail` (already filtered for availability).
  const pickOne = (avail: Cell[]): Cell => {
    const total = avail.reduce((s, c) => s + c.w, 0);
    let t = Math.random() * total;
    for (const c of avail) {
      t -= c.w;
      if (t <= 0) return c;
    }
    return avail[avail.length - 1];
  };

  // Place a random subset (MAX_VISIBLE) of the inventory into distinct cells; the
  // rest stay hidden and rotate in via the reshuffle tick.
  const layout = (pool: Cell[]): Placement[] => {
    const occupied = new Set<string>();
    const out: Placement[] = [];
    const ids = INVENTORY.map((_, id) => id)
      .sort(() => Math.random() - 0.5)
      .slice(0, MAX_VISIBLE);
    ids.forEach((id) => {
      const avail = pool.filter((c) => !occupied.has(`${c.g},${c.r}`));
      if (!avail.length) return;
      const pick = pickOne(avail);
      occupied.add(`${pick.g},${pick.r}`);
      out.push({ ...INVENTORY[id], id, gcol: pick.g, row: pick.r, key: keyRef.current++, delay: Math.random() * 2 });
    });
    return out;
  };

  const rebuild = () => {
    const pool = computePool();
    poolRef.current = pool;
    if (pool.length) setPlacements(layout(pool));
  };

  // ---- lifecycle -----------------------------------------------------------

  useEffect(() => {
    rebuild();
    // Re-measure once fonts settle (headline metrics shift the content box).
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(rebuild).catch(() => {});

    let raf = 0;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(rebuild);
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotation tick: fade one visible item out, then swap it for a different hidden
  // inventory item somewhere else. Keeps the field sparse and ever-changing rather
  // than showing labels + legacy cubes + 3D cubes all at once. The 3D cube fields
  // are rarely chosen to retire, so they linger far longer than the flat glyphs.
  useEffect(() => {
    if (!placements.length) return;
    const id = setInterval(() => {
      // Phase 1 — mark one retireable item as leaving (it fades out via CSS).
      setPlacements((prev) => {
        const retireable = prev.filter(
          (p) => !p.leaving && !(p.type === 'cube3d' && Math.random() < 0.85),
        );
        if (retireable.length <= 2) return prev;
        const victim = retireable[Math.floor(Math.random() * retireable.length)];
        return prev.map((p) => (p === victim ? { ...p, leaving: true } : p));
      });

      // Phase 2 — after the fade, drop the leaver and bring in a hidden item.
      setTimeout(() => {
        setPlacements((prev) => {
          const staying = prev.filter((p) => !p.leaving);
          const pool = poolRef.current;
          if (!pool.length || staying.length >= MAX_VISIBLE) return staying;
          const occupied = new Set(staying.map((p) => `${p.gcol},${p.row}`));
          const activeIds = new Set(staying.map((p) => p.id));
          const hidden = INVENTORY.map((item, id) => ({ item, id })).filter(
            (x) => !activeIds.has(x.id),
          );
          if (!hidden.length) return staying;
          const choice = hidden[Math.floor(Math.random() * hidden.length)];
          const avail = pool.filter((c) => !occupied.has(`${c.g},${c.r}`));
          if (!avail.length) return staying;
          const cell = pickOne(avail);
          return [
            ...staying,
            { ...choice.item, id: choice.id, gcol: cell.g, row: cell.r, key: keyRef.current++, delay: Math.random() * 1.2 },
          ];
        });
      }, FADE_OUT_MS);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [placements.length]);

  // ---- render --------------------------------------------------------------

  const renderInner = (p: Placement) => {
    switch (p.type) {
      case 'cube':
        return <CubeCluster variant={p.variant} />;
      case 'cube3d':
        // Small 3D cube field, centered with margin inside its grid square, that
        // tilts to follow the cursor anywhere on the page. Placed by the same
        // random/collision-avoiding system, so it never shares a cell with a label.
        return (
          <div
            className="flex items-center justify-center"
            style={{ width: 'var(--grid-cell, 72px)', height: 'var(--grid-cell, 72px)' }}
          >
            <Cubes
              gridSize={3}
              maxAngle={55}
              cellGap={5}
              faceColor="rgba(203,213,225,0.5)"
              borderStyle="1px solid rgba(215,14,98,0.4)"
              rippleColor="#d70e62"
              autoAnimate={false}
              followPointer
              followRange={520}
              followStagger={0.03}
              twinkle
              className="w-[42px] h-[42px]"
            />
          </div>
        );
      case 'star':
        return <StarMarker size={p.size} delay={p.delay} />;
      case 'panel':
        return <DashedPanel cellsW={1} cellsH={1} label={p.label} />;
      case 'label':
        return <FloatingLabel text={p.text} position={p.position} />;
    }
  };

  return (
    <div ref={rootRef} aria-hidden className="absolute inset-0 overflow-hidden pointer-events-none">
      {placements.map((p) => (
        <span
          key={`${p.id}-${p.key}`}
          className={`absolute ${
            p.leaving ? 'animate-deco-out' : p.type === 'cube3d' ? '' : 'animate-deco-fade'
          }`}
          style={{
            left: gridX(p.gcol),
            top: cellExpr({ cells: p.row }),
            animationDelay: p.leaving || p.type === 'cube3d' ? undefined : `${p.delay * 0.15}s`,
          }}
        >
          {renderInner(p)}
        </span>
      ))}
    </div>
  );
};

export default HeroDecorations;
